import { useEffect, useState } from "react";
import { Heart, Trophy } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCountdown } from "@/lib/contest";

const Burst = () => (
  <span className="pointer-events-none absolute inset-0" aria-hidden>
    {Array.from({ length: 14 }, (_, i) => (
      <Heart key={i} className="thank-heart absolute left-1/2 top-1/2 w-5 h-5 text-white" fill="currentColor" style={{ "--a": `${i * 26}deg`, animationDelay: `${(i % 4) * 50}ms` }} />
    ))}
  </span>
);

const weekLabel = (w) => {
  const [y, n] = w.split("-W").map(Number);
  const jan4 = new Date(Date.UTC(y, 0, 4));
  const mon = new Date(jan4.getTime() - ((jan4.getUTCDay() || 7) - 1) * 864e5 + (n - 1) * 7 * 864e5);
  const sun = new Date(mon.getTime() + 6 * 864e5);
  const f = (d) => d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", timeZone: "UTC" });
  return `KW ${n} (${f(mon)}–${f(sun)})`;
};

export function MegaHeartCard() {
  const { setUser } = useAuth();
  const [m, setM] = useState(null);
  const [busy, setBusy] = useState(false);
  const [burst, setBurst] = useState(0);
  const [rec, setRec] = useState(false);
  useEffect(() => { api.get("/megaheart").then(({ data }) => setM(data)).catch(() => {}); }, []);
  const { d, h } = useCountdown(m?.ends_at);
  if (!m) return null;
  const pct = Math.min(100, (m.taps / m.goal) * 100);
  const tap = async () => {
    if (m.tapped_today || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/megaheart/tap");
      setM(data);
      setBurst((b) => b + 1);
      if (data.just_bonus) { setUser((u) => ({ ...u, points: (u.points || 0) + data.just_bonus })); toast.success(`Ziel geschafft! +${data.just_bonus} Punkte für dich 💖`); }
      else toast.success("Dein Herzschlag zählt! Morgen wieder tippen 💖");
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="mega-heart relative overflow-hidden rounded-3xl p-5 text-white" data-testid="mega-heart-card" data-urgent={!m.tapped_today && !m.done ? "1" : undefined}>
      <div className="relative flex items-center gap-4">
        <button onClick={tap} disabled={busy || m.tapped_today} aria-label="Aufs Mega-Herz tippen" data-testid="mega-heart-tap"
          className={`relative shrink-0 w-24 h-24 rounded-full flex items-center justify-center transition-transform active:scale-90 ${m.tapped_today ? "bg-white/15" : "bg-white/25 hover:scale-105"}`}>
          {burst > 0 && <Burst key={burst} />}
          <Heart className={`w-16 h-16 drop-shadow-[0_0_18px_rgba(255,255,255,.8)] ${m.tapped_today ? "" : "heart-beat"}`} fill="currentColor" />
        </button>
        <div className="min-w-0 flex-1 space-y-1">
          <p className="text-[11px] font-extrabold tracking-[0.2em] text-white/80">DAS MEGA-HERZ</p>
          <h3 className="font-extrabold text-lg leading-tight" data-testid="mega-heart-title">
            {m.done ? "Geschafft! 🎉" : m.tapped_today ? "Danke für deinen Herzschlag!" : "Tipp aufs Herz!"}
          </h3>
          <p className="text-xs text-white/85">{m.done ? `Belohnung: ${m.reward}` : `Gemeinsam ${m.goal.toLocaleString("de-DE")} Herzschläge bis Sonntag: ${m.reward}`}</p>
        </div>
      </div>
      <div className="relative mt-4 space-y-1.5">
        <div className="h-3 rounded-full bg-white/25 overflow-hidden"><div className="h-full rounded-full bg-white transition-[width] duration-700" style={{ width: `${pct}%` }} data-testid="mega-heart-progress" /></div>
        <div className="flex justify-between text-[11px] font-bold text-white/90">
          <span data-testid="mega-heart-count">{m.taps.toLocaleString("de-DE")} / {m.goal.toLocaleString("de-DE")} 💖 · {m.tappers} dabei</span>
          <span>{m.done ? `+${m.bonus} Punkte für alle Mittipper` : `noch ${d > 0 ? `${d} T ` : ""}${h} Std`}</span>
        </div>
        {!m.done && <p className="text-[11px] text-white/75">1 Tipp pro Tag · Bei Erfolg +{m.bonus} Punkte für alle, die mitgetippt haben</p>}
        {m.records?.length > 0 && <button onClick={() => setRec((v) => !v)} data-testid="mega-heart-records-toggle" className="text-[11px] font-bold underline underline-offset-2 text-white/90 inline-flex items-center gap-1"><Trophy className="w-3.5 h-3.5" /> {rec ? "Rekorde ausblenden" : "Rekord-Wochen ansehen"}</button>}
        {rec && (
          <ol className="mt-1 space-y-1 rounded-2xl bg-white/15 p-3" data-testid="mega-heart-records">
            {m.records.map((r, i) => (
              <li key={r.week} className={`flex items-center gap-2 text-xs ${r.current ? "font-extrabold" : ""}`} data-testid={`mega-heart-record-${i}`}>
                <span className="w-5 text-center">{["🥇", "🥈", "🥉"][i] || `${i + 1}.`}</span>
                <span className="flex-1">{weekLabel(r.week)}{r.current && " · diese Woche"}</span>
                <span>{r.taps.toLocaleString("de-DE")} 💖{r.done && " ✓"}</span>
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}
